import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Calendar, Download, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { productionReportApiService, WorkerDailyPerformanceDto } from '../services/productionReportApi';
import { workerApiService, Worker } from '../services/workerApi';
import { getPaginationConfig } from '../config/uiConfig';

const WorkerPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [reportData, setReportData] = useState<WorkerDailyPerformanceDto[]>([]);

  const now = new Date();
  const getCurrentMonthStart = () => {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  };
  const getTodayDate = () => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  };

  const [selectedWorkerId, setSelectedWorkerId] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(getCurrentMonthStart());
  const [endDate, setEndDate] = useState<string>(getTodayDate());

  const paginationConfig = getPaginationConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const branchId = selectedBranch || user?.defaultBranchId;

  useEffect(() => {
    if (!branchId) return;
    setWorkersLoading(true);
    workerApiService
      .getWorkers(branchId)
      .then(setWorkers)
      .catch(() => setWorkers([]))
      .finally(() => setWorkersLoading(false));
  }, [branchId]);

  // By default load data for all workers for the selected date range when branch is available
  useEffect(() => {
    if (!branchId || !startDate || !endDate) return;
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const loadReportData = async () => {
    if (!branchId) {
      toast.error('Please select a branch');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    const workerId = selectedWorkerId === 'all' ? undefined : parseInt(selectedWorkerId, 10);

    setLoading(true);
    try {
      const response = await productionReportApiService.getWorkerDailyPerformance({
        branchId,
        workerId: Number.isNaN(workerId) ? undefined : workerId,
        startDate,
        endDate,
      });
      if (response.success && response.data) {
        setReportData(Array.isArray(response.data) ? response.data : []);
        setCurrentPage(1);
      } else {
        setReportData([]);
      }
    } catch {
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return reportData;
    const key = sortColumn as keyof WorkerDailyPerformanceDto;
    return [...reportData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [reportData, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 text-primary-600" /> : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleExport = () => {
    if (sortedData.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      const headers = ['Worker Name', 'Performance Date', 'Endgood Name', 'Process Name', 'Quantity'];
      const csvRows = [
        headers.join(','),
        ...sortedData.map((item) =>
          [
            item.workerName ?? '',
            item.performanceDate ?? '',
            item.endgoodName ?? '',
            item.processName ?? '',
            (item.quantity ?? 0).toString(),
          ]
            .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(',')
        ),
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `Worker_Performance_${timestamp}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${sortedData.length} records to CSV`);
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Worker Performance Report</h1>
            <p className="text-gray-600 mt-1">Worker output by date with endgood, process, and quantity</p>
          </div>
        </div>
        {sortedData.length > 0 && (
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-primary-200 text-primary-600 hover:bg-primary-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        )}
      </div>

      <Card className="border-primary-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="mb-2 block">Worker</Label>
              <Select value={selectedWorkerId} onValueChange={setSelectedWorkerId} disabled={workersLoading}>
                <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                  <SelectValue placeholder="All workers" />
                </SelectTrigger>
                <SelectContentWithSearch searchPlaceholder="Search workers..." debounceMs={300}>
                  <SelectItem value="all">All workers</SelectItem>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id.toString()}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContentWithSearch>
              </Select>
            </div>
            <div>
              <Label htmlFor="wp-startDate" className="mb-2 block">Start Date *</Label>
              <Input
                id="wp-startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div>
              <Label htmlFor="wp-endDate" className="mb-2 block">End Date *</Label>
              <Input
                id="wp-endDate"
                type="date"
                value={endDate}
                max={getTodayDate()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v <= getTodayDate()) setEndDate(v);
                  else toast.error('End date cannot be after today');
                }}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={loadReportData}
                disabled={loading || !startDate || !endDate}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load Report'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-3 text-gray-600">Loading worker performance...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && sortedData.length > 0 && (
        <>
          <Card className="border-primary-200 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary-600" />
                  Worker Performance Data
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-600">Items per page:</Label>
                    <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paginationConfig.itemsPerPageOptions.map((opt) => (
                          <SelectItem key={opt} value={opt.toString()}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-sm text-gray-600">
                    Showing {startIndex + 1} – {Math.min(endIndex, sortedData.length)} of {sortedData.length} records
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('workerName')}
                      >
                        <div className="flex items-center gap-2">
                          Worker Name
                          {getSortIcon('workerName')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('performanceDate')}
                      >
                        <div className="flex items-center gap-2">
                          Performance Date
                          {getSortIcon('performanceDate')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('endgoodName')}
                      >
                        <div className="flex items-center gap-2">
                          Endgood Name
                          {getSortIcon('endgoodName')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('processName')}
                      >
                        <div className="flex items-center gap-2">
                          Process Name
                          {getSortIcon('processName')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-primary-100/50 text-right"
                        onClick={() => handleSort('quantity')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Quantity
                          {getSortIcon('quantity')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index} className="hover:bg-primary-50/50">
                        <TableCell className="font-medium">{item.workerName ?? 'N/A'}</TableCell>
                        <TableCell>
                          {item.performanceDate
                            ? new Date(item.performanceDate).toISOString().slice(0, 10)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{item.endgoodName ?? 'N/A'}</TableCell>
                        <TableCell>{item.processName ?? 'N/A'}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}

      {!loading && sortedData.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No data. Select worker (or All), date range, and click Load Report.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkerPerformancePage;
